import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, Router, UrlTree } from '@angular/router';
import { Observable } from 'rxjs';
import { CognitoAuthService } from '@auth/services/cognito-auth.service';
import { CardTypeEnum, NavigationKey } from '@generated/graphql';
import { concatMap, map, take } from 'rxjs/operators';
import { NavigationService } from '@app/shared/services/navigation.service';
import { availableBoards, defaultShellKey } from '@app/pages/pipeline/pipeline-shells';

/**
 * The search shell renders flight requests instead of a pipeline, so a card deep-link
 * lands on the card's own page. EC-458: the shell is the PIPELINE_SEARCH navigation key.
 *
 * EC-758: a user may hold several shells, and the first held is the default. The search
 * redirect fires only when search is the default: a sales board opens `?type=&id=` cards
 * itself. A user with two boards is sent from bare `/pipeline` to the default board's own
 * route with the same query params; with one board `/pipeline` renders in place as before.
 * That redirect needs the PIPELINE section key: without it NavigationGuard refuses bare
 * `/pipeline`, and the boards' own routes would refuse the redirect too.
 */
@Injectable({
  providedIn: 'root',
})
export class PipelineAuthGuard {
  constructor(
    private cognitoAuthService: CognitoAuthService,
    private router: Router,
    private navigationService: NavigationService,
  ) {}

  canActivate(
    route: ActivatedRouteSnapshot,
  ): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree {
    return this.cognitoAuthService.hasCognitoSession().pipe(
      concatMap(() => this.navigationService.navigation$),
      take(1),
      map((keys) => {
        if (defaultShellKey(keys) === NavigationKey.PipelineSearch) {
          if (route.queryParams.type === CardTypeEnum.FlightRequest) {
            void this.router.navigate(['flight-requests', route.queryParams.id]);

            return false;
          }
          if (route.queryParams.type === CardTypeEnum.ChangeRequest) {
            void this.router.navigate(['change-requests', route.queryParams.id]);

            return false;
          }
        }

        const boards = availableBoards(keys);

        if (keys.includes(NavigationKey.Pipeline) && boards.length > 1) {
          // Boards come in BOARD_SHELLS order, which mirrors the backend's shell order, so
          // the first is the default board.
          return this.router.createUrlTree([boards[0].path], {
            queryParams: route.queryParams,
          });
        }

        return true;
      }),
    );
  }
}
